'use client'

import React from 'react'

import useMergedRefs from '../../hooks/use-merge-refs'
import useOpenWithDelays from '../../hooks/use-open-with-delays'
import usePopover, {useClosePopoverOnOutsideClick} from '../../hooks/use-popover'
import usePopoverPosition from '../../hooks/use-popover-position'
import useStackedCloseOnEscape from '../../hooks/use-stacked-close-on-escape'
import useTrackRef from '../../hooks/use-track-ref'
import {Rectangle} from '../../services/dom-observer'
import {
  PopoverAnchorPlacement,
  defaultPlacementFallbacks,
  positionPopover,
} from '../../services/position-popover'

import {TooltipPlacement} from '../tooltip/Tooltip'

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const getTooltipPlacementAndShift = (
  anchorRect: Rectangle | null,
  popoverRect: Rectangle | null
) => {
  if (!anchorRect || !popoverRect) {
    return {
      placement: TooltipPlacement.top,
      shift: 0,
    }
  }

  const arrowPadding = 15

  const shiftX = clamp(
    anchorRect.x + anchorRect.width / 2 - popoverRect.x - popoverRect.width / 2,
    popoverRect.width / -2 + arrowPadding,
    popoverRect.width / 2 - arrowPadding
  )

  const shiftY = clamp(
    anchorRect.y + anchorRect.height / 2 - popoverRect.y - popoverRect.height / 2,
    popoverRect.height / -2 + arrowPadding,
    popoverRect.height / 2 - arrowPadding
  )

  if (anchorRect.y > popoverRect.y + popoverRect.height) {
    return {
      placement: TooltipPlacement.top,
      shift: shiftX,
    }
  }

  if (anchorRect.y + anchorRect.height < popoverRect.y) {
    return {
      placement: TooltipPlacement.bottom,
      shift: shiftX,
    }
  }

  if (anchorRect.x > popoverRect.x + popoverRect.width) {
    return {
      placement: TooltipPlacement.left,
      shift: shiftY,
    }
  }

  if (anchorRect.x + anchorRect.width < popoverRect.x) {
    return {
      placement: TooltipPlacement.right,
      shift: shiftY,
    }
  }

  return {
    placement: TooltipPlacement.top,
    shift: 0,
  }
}

export interface ChildrenRenderProps<T extends HTMLElement> {
  close: () => void
  open: () => void
  toggle: () => void
  isOpen: boolean
  ref: React.RefCallback<T | null>
}

export interface TooltipRenderProps<T extends HTMLElement> {
  close: () => void
  open: () => void
  toggle: () => void
  isOpen: boolean
  ref: React.RefCallback<T | null>
  style: React.CSSProperties
  placement: TooltipPlacement
  shift: number
}

export interface Props<TAnchor extends HTMLElement, TTooltip extends HTMLElement> {
  /**
   * Tooltip state can be controlled from outside or left uncontrolled.
   * If isOpen is set to undefined an internal state will handle opening and closing tooltips automatically.
   */
  isOpen?: boolean
  /**
   * Prevents opening the tooltip on touch devices with a tab.
   * Only relevant if isOpen is undefined.
   */
  preventTabbing?: boolean
  /**
   * The anchor element which will trigger the tooltip on hover or focus.
   * Can be a React element or a function that receives the tooltip state and a ref to the anchor element.
   * In case of a React element it has to support ref forwarding to the actual DOM element.
   */
  children:
    | React.ReactElement<{
        ref: React.Ref<TAnchor>
      }>
    | ((params: ChildrenRenderProps<TAnchor>) => React.ReactElement)
  /**
   * The tooltip element which will be shown on hover or focus of the anchor element.
   * Can be a React element or a function that receives the tooltip state and a ref to the tooltip element.
   * In case of a React element it has to support all props of the `Tooltip` component.
   * In case of a function make sure to forward the ref, style, placement and shift props accordingly.
   */
  tooltip:
    | React.ReactElement<{
        ref: React.Ref<TTooltip>
        style: React.CSSProperties
        placement: TooltipPlacement
        shift: number
      }>
    | ((params: TooltipRenderProps<TTooltip>) => React.ReactElement)
  /** Preferred placement of the tooltip relative to its anchor.  */
  placement?: PopoverAnchorPlacement
  /**
   * When the tooltip is open a timer starts.
   * If the user leaves the tooltip or its anchor element the tooltip will close immediately after this timeout is reached.
   * If the tooltip is used in controlled mode this timeout is not applied.
   */
  keepOpenDelay?: number
  /**
   * Opening of the tooltip is delayed by this amount of time when hovered.
   * If the tooltip is opened manually this delay is not applied.
   * If the tooltip is used in controlled mode this delay is not applied.
   */
  delayOnHover?: number
  /**
   * Opens the tooltip only when the anchor element is clicked or tapped.
   */
  openOnClick?: boolean
  /**
   * If used controlled this callback is called when isOpen should change.
   * If used uncontrolled this callback is called when isOpen changed.
   */
  onOpenChange?: (isOpen: boolean) => void
  /** If true closes tooltip when user clicks outside of popover and child content. */
  closeOnOutsideClick?: boolean
  /** Closes the tooltip on Escape key press if the tooltip is the topmost element. */
  closeOnEscape?: boolean
  /**
   * Provide a ref to get access to the tooltip element.
   * This is completely optional and only needed for use cases
   * where access to the tooltip element is needed outside of the TooltipHandler and its render functions.
   */
  tooltipRef?: React.Ref<TTooltip>
  /**
   * Provide a ref to get access to the anchor element.
   * This is completely optional and only needed for use cases
   * where access to the anchor element is needed outside of the TooltipHandler and its render functions.
   */
  anchorRef?: React.Ref<TAnchor>
}

/**
 * General tooltip handler which opens/closes the tooltip based on mouse hover and focus state.
 * This open state can also be controlled from outside if provided.
 */
const TooltipHandler = <TAnchor extends HTMLElement, TTooltip extends HTMLElement>({
  isOpen,
  preventTabbing = false,
  children,
  tooltip,
  placement = PopoverAnchorPlacement.topCenter,
  openOnClick = false,
  closeOnOutsideClick = true,
  closeOnEscape = true,
  keepOpenDelay = openOnClick ? 5000 : 500,
  delayOnHover = 0,
  onOpenChange,
  tooltipRef,
  anchorRef,
}: Props<TAnchor, TTooltip>) => {
  const {
    isOpen: isInnerOpen,
    open: openTooltip,
    close: closeTooltip,
  } = useOpenWithDelays({isOpen, onOpenChange})

  const onPopoverOpenChange = (value: boolean) => {
    if (value) {
      openTooltip()
    } else {
      closeTooltip()
    }
  }

  const {
    isOpen: isPopoverOpen,
    ref: innerPopoverRef,
    openPopover,
    closePopover,
    togglePopover,
    renderPopover,
  } = usePopover<TTooltip>({isOpen: isInnerOpen, onOpenChange: onPopoverOpenChange})

  const {
    popoverRef: popoverPositionRef,
    anchorRef: innerAnchorRef,
    popoverRefObject,
    anchorRefObject,
    popoverStyle,
    anchorRect,
    popoverRect,
  } = usePopoverPosition<TAnchor, TTooltip>({
    isOpen: isPopoverOpen,
    positionHandler: positionPopover([placement, ...defaultPlacementFallbacks[placement]], 15),
  })

  useClosePopoverOnOutsideClick(
    anchorRefObject,
    popoverRefObject,
    closePopover,
    closeOnOutsideClick && isPopoverOpen
  )

  useStackedCloseOnEscape(isPopoverOpen, closePopover, closeOnEscape)

  const preventTabbingRef = React.useRef(preventTabbing)
  preventTabbingRef.current = preventTabbing

  const openOnClickRef = React.useRef(openOnClick)
  openOnClickRef.current = openOnClick

  const anchorEventsRef = useTrackRef(element => {
    const onPointerEnter = (event: PointerEvent) => {
      if (
        preventTabbingRef.current &&
        event instanceof PointerEvent &&
        event.pointerType === 'touch'
      ) {
        event.preventDefault()
        return
      }

      if (!openOnClickRef.current) {
        openTooltip({delay: delayOnHover})
      }
    }

    const onFocus = () => {
      openTooltip()
    }

    const close = () => closeTooltip({keepOpenDelay})

    const onPointerDown = (event: PointerEvent) => {
      if (preventTabbingRef.current && event.pointerType === 'touch') {
        event.preventDefault()
        return
      }

      openTooltip()
    }

    element.addEventListener('pointerdown', onPointerDown)
    element.addEventListener('pointerenter', onPointerEnter)
    element.addEventListener('focus', onFocus)
    element.addEventListener('pointerleave', close)
    element.addEventListener('blur', close)

    return () => {
      element.removeEventListener('pointerdown', onPointerDown)
      element.removeEventListener('pointerenter', onPointerEnter)
      element.removeEventListener('focus', onFocus)
      element.removeEventListener('pointerleave', close)
      element.removeEventListener('blur', close)
    }
  }, isOpen === undefined)

  const finalAnchorRef = useMergedRefs(anchorRef, innerAnchorRef, anchorEventsRef)
  const finalPopoverRef = useMergedRefs(tooltipRef, innerPopoverRef, popoverPositionRef)

  const {placement: tooltipPlacement, shift: tooltipShift} = getTooltipPlacementAndShift(
    anchorRect,
    popoverRect
  )

  const childElement =
    typeof children === 'function'
      ? children({
          close: closePopover,
          open: openPopover,
          toggle: togglePopover,
          isOpen: isPopoverOpen,
          ref: finalAnchorRef,
        })
      : React.cloneElement(children, {ref: finalAnchorRef})

  const tooltipElement =
    typeof tooltip === 'function'
      ? tooltip({
          close: closePopover,
          open: openPopover,
          toggle: togglePopover,
          isOpen: isPopoverOpen,
          ref: finalPopoverRef,
          style: popoverStyle,
          placement: tooltipPlacement,
          shift: tooltipShift,
        })
      : React.cloneElement(tooltip, {
          ref: finalPopoverRef,
          style: popoverStyle,
          placement: tooltipPlacement,
          shift: tooltipShift,
        })

  return (
    <>
      {childElement}
      {renderPopover(tooltipElement)}
    </>
  )
}

export default TooltipHandler
